import { useElementScrollState } from '@lemonade-hq/cdk';
import { clsx } from 'clsx';
import type { ComponentProps } from 'react';
import React, { useCallback, useEffect } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { spacing } from '../../../../styles/spacing.css';
import { Layout } from '../../../Layout/Layout';
import { Text } from '../../../Text/Text';
import { useSelectByScroll } from '../hooks/useSelectByScroll';
import type { CalendarView, Month, Year } from '../types';
import * as styles from './CalendarViews.css';

export interface CalendarSelectProps<Item> extends Pick<ComponentProps<'div'>, 'className'> {
  readonly items: Item[];
  readonly onItemChange: (index: number) => void;
  readonly isItemSelected: (item: Item) => boolean;
  readonly setCalendarView?: (view: CalendarView) => void;
  readonly containerClassName?: string;
  readonly selectedClassName?: string;
  readonly containerOffset?: number;
  readonly containerGap?: number;
  readonly numberOfItemsInRow?: number;
  readonly isScrolling?: boolean;
  readonly selectedIndex?: number;
  readonly shouldScrollToSelectedItem?: boolean;
  readonly itemHeight: number;
  readonly isSideBySideView?: boolean;
}

export const CalendarSelect = <TItem extends Month | Year>({
  items,
  onItemChange,
  isItemSelected,
  containerClassName,
  className,
  selectedClassName = '',
  numberOfItemsInRow = 1,
  selectedIndex = 0,
  itemHeight,
  isSideBySideView,
  containerGap = 0,
}: CalendarSelectProps<TItem>): React.ReactElement => {
  const { ref: selectRef } = useSelectByScroll({
    shouldSelect: Boolean(isSideBySideView),
    onSelect: onItemChange,
    itemHeight,
  });
  const { ref: scrollingRef, isScrolling, hasReachedScrollEnd } = useElementScrollState();

  useEffect(() => {
    if (selectedIndex === -1 || isScrolling) return;

    const localRef = scrollingRef;
    const { current } = localRef;

    /* istanbul ignore next -- @preserve - can't mock null ref */
    const containerHeight = current?.clientHeight ?? 0;
    const containerOffset = isSideBySideView ? 0 : (containerHeight + itemHeight) / 2;

    current?.scrollTo({
      top: Math.ceil(selectedIndex / numberOfItemsInRow) * (itemHeight + containerGap) - containerOffset,
      behavior: 'instant',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = useCallback(
    (index: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      onItemChange(index);
      e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [onItemChange],
  );

  return (
    <Layout
      className={clsx(
        containerClassName,
        isScrolling &&
          !hasReachedScrollEnd &&
          styles.gradientWrapper({
            type: isSideBySideView ? 'sideBySide' : 'singular',
          }),
      )}
      display="grid"
      gridTemplateColumns={`repeat(${numberOfItemsInRow}, 1fr)`}
      maxHeight={styles.CONTAINER_HEIGHT}
      overflow="auto"
      padding={{ lg: `0 ${spacing.s16}` }}
      ref={mergeRefs([scrollingRef, selectRef])}
      role="grid"
      width="100%"
    >
      {items.map((item, index) => (
        <Layout
          as="button"
          className={clsx(className, {
            [selectedClassName]: isItemSelected(item),
          })}
          cursor="pointer"
          disabled={item.isDisabled}
          height={styles.ITEM_HEIGHT}
          key={item.formatted}
          onClick={onClick(index)}
          padding={{ md: `${spacing.s12} ${spacing.s16}` }}
          type="button"
        >
          <Text type="bodyMedium">{item.formatted}</Text>
        </Layout>
      ))}
    </Layout>
  );
};
